import moment from 'moment';

import { TotemMapper } from '@src/models/mappers/TotemMapper';
import type { Totem, TotemWithCreator } from '@src/models/interfaces/totem.interface';
import type { TUserOrganization } from '@src/models/interfaces/user.interface';
import TotemRepository from '@src/repositories/TotemRepository';
import UserRepository from '@src/repositories/UserRepository';
import safeExecute from '@src/util/safeExecute';

const resolveOrgUserDisplayName = (user: TUserOrganization | null, fallbackId: string): string => {
  if (!user) {
    return fallbackId;
  }

  const fromNames = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return fromNames || user.email?.trim() || fallbackId;
};

class TotemService {
  private repository: TotemRepository;
  private userRepository: UserRepository;

  constructor(repository = new TotemRepository(), userRepository = new UserRepository()) {
    this.repository = repository;
    this.userRepository = userRepository;
  }

  private async resolveCreatorDisplayNames(
    organizationId: string,
    userIds: string[],
  ): Promise<Map<string, string>> {
    const uniqueIds = userIds.filter(
      (userId, index, ids) => Boolean(userId) && ids.indexOf(userId) === index,
    );
    const displayNames = new Map<string, string>();

    await Promise.all(
      uniqueIds.map(async userId => {
        const creator = await this.userRepository.getUser(organizationId, userId);
        displayNames.set(userId, resolveOrgUserDisplayName(creator, userId));
      }),
    );

    return displayNames;
  }

  private withCreator(totem: Totem, creatorDisplayNames: Map<string, string>): TotemWithCreator {
    const creatorDisplayName = totem.createdByUserId
      ? (creatorDisplayNames.get(totem.createdByUserId) ?? totem.createdByUserId)
      : '-';

    return {
      ...totem,
      creatorDisplayName,
    };
  }

  async listTotemsWithCreators(organizationId: string): Promise<TotemWithCreator[]> {
    return safeExecute(async () => {
      const items = await this.repository.listByOrganizationId(organizationId);
      const totems = items.map(({ id, record }) => TotemMapper.fromFirestore(id, record));
      const creatorDisplayNames = await this.resolveCreatorDisplayNames(
        organizationId,
        totems.map(totem => totem.createdByUserId).filter((id): id is string => Boolean(id)),
      );

      return totems
        .map(totem => this.withCreator(totem, creatorDisplayNames))
        .sort((a, b) => {
          const aMs = a.createdAt?.getTime() ?? 0;
          const bMs = b.createdAt?.getTime() ?? 0;
          return bMs - aMs;
        });
    }, 'TotemService.listTotemsWithCreators', { organizationId });
  }

  async createTotem(
    organizationId: string,
    name: string,
    startAt: moment.Moment,
    expiresIn?: number | null,
  ): Promise<TotemWithCreator[]> {
    return safeExecute(async () => {
      const params = TotemMapper.toCreateTotemParams(organizationId, name, startAt, expiresIn);
      await this.repository.create(params);
      return this.listTotemsWithCreators(organizationId);
    }, 'TotemService.createTotem', { organizationId, expiresIn: expiresIn ?? null });
  }

  async revokeTotem(organizationId: string, jti: string): Promise<void> {
    return safeExecute(async () => {
      await this.repository.revoke({ organizationId, jti });
    }, 'TotemService.revokeTotem', { organizationId, jti });
  }
}

export default TotemService;
